import uuid from 'uuid/v4'
import moment from 'moment'
import { getOffers } from '@/menu/offers/offers-service'
import { initSection } from '@/menu/common/utils/menu-initializer-util'
import { getPluNumber } from '@/menu/menu-service'
import { LAYOUT_BUTTON_COLORS } from '@/common/colors'
import _ from 'lodash'
import { getDefaultPricingScheme } from '@/menu/pricing-schemes/pricing-schemes-service'

export function initOffers (menuBlob) {
  if (!menuBlob.hasOwnProperty('offers')) {
    menuBlob.offers = []
  }
}

export function newOffer (menuBlob, isOverride) {
  const offer = {
    id: uuid(),
    entityType: 'OFFER',
    dateCreated: moment().format('YYYY-MM-DD'),
    order: getOffers(menuBlob).length,
    ...(isOverride ? { $added: true } : {})
  }

  initOffer(offer, () => { return getPluNumber(menuBlob, 'OFFER_INFO', offer.id) }, -1)
  return offer
}

/**
 * @param offer a partially constructed offer, possibly empty ({})
 * @param pluGen a no-arg function that returns a PLU number
 * @param defPricingSchemeId the id of the default pricing scheme, for
 *        initializing the value section if it doesn't exist
 */
export function initOffer(offer, pluGen, defPricingSchemeId) {

  initOfferInfo(offer, pluGen)
  initOfferApplying(offer)
  initOfferValidity(offer)
  initSection(offer, 'itemGroups', () => [])
  initOfferValue(offer, defPricingSchemeId)
  initOfferInteractions(offer)
  initOfferAdvanced(offer)

  return offer
}

export function initOfferForMenu (offer, menuBlob) {

  function pluGen() {
    return getPluNumber(menuBlob, 'OFFER_INFO', offer.id)
  }

  let defPricingSchemeId = getDefaultPricingScheme(menuBlob).id

  initOffer(offer, pluGen, defPricingSchemeId)
}

function initOfferInfo (offer, pluGen) {
  initSection(offer, 'info', () => ({
    entityType: 'OFFER_INFO',
    id: offer.id,
    name: '',
    displayName: '',
    description: '',
    pluNum: pluGen(),
    status: 'ACTIVE',
    channels: ['POS', 'ONLINE'],
    type: 'COUPON',
    imageId: null,
    campaigns: [],
    shortUrl: ''
  }))
}

function initOfferApplying (offer) {
  initSection(offer, 'applying', () => ({
    entityType: 'OFFER_APPLYING',
    autoApplyIfCriteriaMet: false,
    promptIfAutoApplied: false,
    showInSmartCoupons: true,
    couponCode: {
      required: false,
      code: ''
    },
    showInDiscountScreenList: false,
    posMenuButton: {
      enabled: true,
      color: LAYOUT_BUTTON_COLORS[_.random(0, 9)]
    },
    createOnlineCouponButton: false
  }))
}

function initOfferValidity (offer) {
  initSection(offer, 'validity', () => ({
    entityType: 'OFFER_VALIDITY',
    dateRange: {
      enabled: false,
      start: null,
      end: null
    },
    daysOfTheWeek: {
      enabled: false, // Default false
      days: []
    },
    timeOfDay: {
      enabled: false,
      startTime: null,
      endOffset: null
    },
    orderTypes: {
      enabled: false,
      allowed: []
    },
    tenderTypes: {
      enabled: false,
      exclude: false,
      selected: []
    },
    customerTypes: {
      enabled: false,
      allowed: ['RESIDENTIAL', 'BUSINESS', 'LODGING']
    },
    customerGroups: {
      enabled: false,
      allowed: []
    },
    minimumPurchase: {
      enabled: false,
      amount: '0.00',
      mode: 'BEFORE'
    },
    usageLimit: {
      enabled: false,
      limit: 1
    }
  }))
}

function initOfferValue (offer, defaultSchemeId) {

  if (!offer.hasOwnProperty('value')) {
    offer.value = {
      entityType: 'OFFER_VALUE',
      appliesTo: 'ENTIRE_ORDER',
      pricingSchemes: [getEmptyPricingSchemeValue(defaultSchemeId)]
    }
  } else {
    if (!offer.value.pricingSchemes.find(it => it.schemeId === defaultSchemeId)) {
      offer.value.pricingSchemes.push(getEmptyPricingSchemeValue(defaultSchemeId))
    }

    for (let pricingScheme of offer.value.pricingSchemes) {
      if (!pricingScheme.hasOwnProperty('anyItem')) {
        pricingScheme.anyItem = emptyAnyItem()
      }
    }
  }
}

export function getEmptyPricingSchemeValue (schemeId) {
  return {
    entityType: 'OFFER_VALUE_PRICING_SCHEME',
    schemeId,
    $identifier: 'schemeId',
    entireOrder: {
      entityType: 'OFFER_VALUE_ENTIRE_ORDER',
      value: {
        entityType: 'OFFER_VALUE_ENTIRE_ORDER_VALUE',
        type: 'AMOUNT',
        amountValue: '0.00',
        percentageValue: '0.00'
      },
      cashierOverride: {
        enabled: false,
        limitOverride: false,
        amountValue: '1.00',
        percentageValue: '1.00'
      },
      freeDelivery: false,
      autoGratuity: false,
      limitOfferValue: {
        enabled: false,
        amount: '1.00'
      },
      excludedChoices: []
    },
    individualGroups: {
      entityType: 'OFFER_VALUE_INDIVIDUAL_GROUPS',
      itemGroups: [],
      freeDelivery: false,
      limitOfferValue: {
        enabled: false,
        amount: '1.00'
      }
    },
    comboOffer: {
      entityType: 'OFFER_VALUE_COMBO',
      itemGroups: [],
      value: {
        type: 'AMOUNT',
        amountValue: '0.00',
        percentageValue: '0.00',
        fixedPrice: '0.00'
      },
      excludeStyleUpcharge: false,
      excludeModifierUpcharges: false,
      limitOfferValue: {
        enabled: false,
        amount: '1.00'
      },
      freeDelivery: false // Defaults false
    },
    anyItem: emptyAnyItem()
  }
}

function emptyAnyItem () {
  return {
    entityType: 'OFFER_VALUE_ANY_ITEM',
    value: {
      entityType: 'OFFER_VALUE_ANY_ITEM_VALUE',
      type: 'AMOUNT',
      amountValue: '0.00',
      percentageValue: '0.00'
    },
    cashierOverride: {
      enabled: false,
      limitOverride: false,
      amountValue: '1.00',
      percentageValue: '1.00'
    },
    freeDelivery: false,
    limitOfferValue: {
      enabled: false,
      amount: '1.00'
    },
    excludedChoices: []
  }
}

function initOfferInteractions (offer) {
  initSection(offer, 'interactions', () => ({
    entityType: 'OFFER_INTERACTIONS',
    notValidWithOffersThat: {
      applyAutomatically: {
        enabled: false,
        limitOfferTypes: ['COUPON', 'DISCOUNT', 'PROMO', 'COMP']
      },
      applyToTheSameItem: {
        enabled: false,
        limitOfferTypes: ['COUPON', 'DISCOUNT', 'PROMO', 'COMP']
      },
      applyToTheOrder: {
        enabled: false,
        limitOfferTypes: ['COUPON', 'DISCOUNT', 'PROMO', 'COMP']
      }
    }
  }))
}

function initOfferAdvanced (offer) {
  initSection(offer, 'advanced', () => ({
    showOfferAs: 'LINE_ITEM',
    reportAsLowerSale: false,
    requireSignature: false,
    physicalCouponExpected: false,
    taxApply: 'OFF',
    applyOfferTo: 'LOWEST',
    requireReason: {
      enabled: false,
      reasons: []
    }
  }))
}
